import { useApiHealthStore } from '@app/store/apiHealthStore';
import { toApiError } from './ApiError';

let installed = false;

/**
 * Safety net for failures that escaped every local handler.
 *
 * An unhandled rejection is what used to reach the user as a full-screen
 * webpack-dev-server overlay in development, and as nothing at all in production.
 * Catching it here means the worst case is a banner and a console entry.
 */
export const installGlobalErrorHandlers = (): void => {
  if (installed) return;
  installed = true;

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = toApiError(event.reason);
    console.error('[Global] Unhandled promise rejection:', error.message, event.reason);

    // If it was an outage, make sure the banner reflects it even though nobody
    // awaited the call that failed.
    useApiHealthStore.getState().reportFailure(error);

    // Users must never see a raw failure, so it stops here in production. In
    // development it is still allowed through to the dev-server overlay: anything
    // reaching this handler is a gap in local handling and worth seeing at once.
    if (process.env.NODE_ENV === 'production') {
      event.preventDefault();
    }
  });

  window.addEventListener('error', (event: ErrorEvent) => {
    // Render errors are handled by ErrorBoundary; this only logs whatever slips past,
    // so a stray script error never leaves the console silent.
    console.error('[Global] Uncaught error:', event.message, event.error);
  });
};
